import styled from '@emotion/styled';
import { Check } from 'lucide-react';
import type { Order } from '@/domain/entities/order.entity';
import { ORDER_STEPS } from './constants';
import { getCompletedStepIndex } from './utils';

const StepperContainer = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  padding: ${({ theme }) => theme.space.lg} 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const StepWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  min-width: 60px;
`;

const StepConnector = styled.div<{ completed: boolean }>`
  position: absolute;
  top: 14px;
  left: calc(-50% + 16px);
  right: calc(50% + 16px);
  height: 2px;
  background: ${({ completed }) => (completed ? '#2f806a' : '#e0e0e0')};
  transition: background 0.3s;
`;

const StepCircle = styled.div<{ state: 'completed' | 'active' | 'pending' }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  z-index: 1;
  transition: all 0.3s;

  ${({ state }) => {
    if (state === 'completed') return `background: #2f806a; color: white;`;
    if (state === 'active') return `background: white; border: 2px solid #2f806a; color: #2f806a;`;
    return `background: #f0f0f0; color: #999;`;
  }}
`;

const StepLabel = styled.span<{ active: boolean }>`
  margin-top: 8px;
  font-size: 11px;
  font-weight: ${({ active }) => (active ? 700 : 400)};
  color: ${({ active }) => (active ? '#2f806a' : '#999')};
  text-align: center;
  line-height: 1.3;
  max-width: 80px;
  word-break: break-word;

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    font-size: 10px;
    max-width: 60px;
  }
`;

interface OrderProgressStepperProps {
  readonly order: Order;
}

export function OrderProgressStepper({ order }: Readonly<OrderProgressStepperProps>) {
  const completedIndex = getCompletedStepIndex(order);

  return (
    <StepperContainer>
      {ORDER_STEPS.map((step, i) => {
        const isCompleted = i <= completedIndex;
        const isActive = i === completedIndex;
        let state: 'completed' | 'active' | 'pending' = 'pending';
        if (isCompleted && isActive) state = 'active';
        else if (isCompleted) state = 'completed';

        return (
          <StepWrapper key={step.label}>
            {i > 0 && <StepConnector completed={i <= completedIndex} />}
            <StepCircle state={state}>
              {state === 'completed' && !isActive ? <Check size={14} /> : i + 1}
            </StepCircle>
            <StepLabel active={isCompleted}>{step.label}</StepLabel>
          </StepWrapper>
        );
      })}
    </StepperContainer>
  );
}
